import { Card } from "@/components/ui/card";
import { MapPin, Truck, Weight, Package, FileText, Calendar, Hash, Clock } from "lucide-react";
import { parseYYYYMMDDToLocalDate } from "@/lib/dateOnly";

function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  const parsed = parseYYYYMMDDToLocalDate(d);
  return parsed ? parsed.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : d;
}

function fmtEquipment(t: string | null | undefined) {
  if (!t) return "—";
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

interface Props {
  load: any;
  loadStops: any[];
  bids?: any[];
  awardedBroker?: {
    user_id: string | null;
    company_name: string | null;
    contact_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
}

export function OverviewTab({ load, loadStops, bids = [], awardedBroker = null }: Props) {
  if (!load) return null;
  const isAwarded = !!load.awarded_bid_id;

  // Awarded loads: the Awarded Broker & Rate card at the top of the page and the
  // formatted dispatch sheet below already carry every detail this tab used to
  // duplicate (lane, freight, schedule, references, stops), so we keep it quiet.
  if (isAwarded) {
    return (
      <Card className="p-4">
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground">Load awarded</p>
            <p className="mt-0.5">
              Broker and rate are shown at the top of this page. Full lane, freight, schedule,
              reference and stop details are in the dispatch sheet below.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-4 lg:col-span-2 border-brand-teal/30">
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <Clock className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground">Not yet awarded</p>
            <p className="mt-0.5">
              Broker and rate will populate automatically when you award a bid.
              {bids.length > 0 ? ` ${bids.length} bid${bids.length === 1 ? "" : "s"} on the board.` : " No bids yet."}
            </p>
          </div>
        </div>
      </Card>

      {/* Lane */}
      <Card className="p-4">
        <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5" /> Lane
        </h3>
        <div className="space-y-3">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">Origin</p>
            <p className="text-sm font-semibold text-foreground">{load.origin_city}, {load.origin_state}</p>
            {load.origin_name && <p className="text-xs text-muted-foreground">{load.origin_name}</p>}
          </div>
          <div className="border-t border-border" />
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">Destination</p>
            <p className="text-sm font-semibold text-foreground">{load.destination_city}, {load.destination_state}</p>
            {load.destination_name && <p className="text-xs text-muted-foreground">{load.destination_name}</p>}
          </div>
          {load.total_miles && (
            <div className="border-t border-border pt-2">
              <p className="text-xs text-muted-foreground">Distance: <span className="font-semibold text-foreground">{Math.round(load.total_miles).toLocaleString()} mi</span></p>
            </div>
          )}
        </div>
      </Card>

      {/* Freight */}
      <Card className="p-4">
        <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5" /> Freight
        </h3>
        <dl className="space-y-2 text-sm">
          <div className="flex items-center justify-between"><dt className="text-muted-foreground flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Equipment</dt><dd className="font-medium">{fmtEquipment(load.equipment_type)}</dd></div>
          <div className="flex items-center justify-between"><dt className="text-muted-foreground flex items-center gap-1.5"><Weight className="h-3.5 w-3.5" /> Weight</dt><dd className="font-medium">{load.weight_lbs ? `${Number(load.weight_lbs).toLocaleString()} lbs` : "—"}</dd></div>
          <div className="flex items-center justify-between"><dt className="text-muted-foreground">Length</dt><dd className="font-medium">{load.length_ft ? `${load.length_ft} ft` : "—"}</dd></div>
          <div className="flex items-center justify-between"><dt className="text-muted-foreground">Commodity</dt><dd className="font-medium">{load.commodity || "—"}</dd></div>
          <div className="flex items-center justify-between"><dt className="text-muted-foreground">Size</dt><dd className="font-medium">{load.load_type || "—"}</dd></div>
          <div className="flex items-center justify-between"><dt className="text-muted-foreground">Load Nature</dt><dd className="font-medium capitalize">{(load as any).load_nature || "spot"}</dd></div>
        </dl>
      </Card>

      {/* Dates + Rate */}
      <Card className="p-4">
        <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" /> Schedule & Rate
        </h3>
        <dl className="space-y-2 text-sm">
          <div className="flex items-center justify-between"><dt className="text-muted-foreground">Pickup</dt><dd className="font-medium">{fmtDate(load.pickup_date)}</dd></div>
          <div className="flex items-center justify-between"><dt className="text-muted-foreground">Delivery</dt><dd className="font-medium">{fmtDate(load.delivery_date)}</dd></div>
          <div className="flex items-center justify-between"><dt className="text-muted-foreground">Target Rate</dt><dd className="font-medium tabular-nums">
            {load.target_rate_min && load.target_rate_max
              ? `$${Number(load.target_rate_min).toLocaleString()} – $${Number(load.target_rate_max).toLocaleString()}`
              : load.target_rate_max
              ? `Up to $${Number(load.target_rate_max).toLocaleString()}`
              : "—"}
          </dd></div>
        </dl>
      </Card>

      {/* References & Notes */}
      <Card className="p-4">
        <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <Hash className="h-3.5 w-3.5" /> References & Notes
        </h3>
        <dl className="space-y-2 text-sm">
          <div className="flex items-center justify-between"><dt className="text-muted-foreground">Reference #</dt><dd className="font-mono text-xs">{load.reference_number || "—"}</dd></div>
          <div className="flex items-center justify-between"><dt className="text-muted-foreground">Load #</dt><dd className="font-mono text-xs">{load.load_number}</dd></div>
        </dl>
        {load.special_instructions && (
          <div className="mt-3 border-t border-border pt-2">
            <p className="text-[10px] text-muted-foreground uppercase mb-1">Special Instructions</p>
            <p className="text-xs text-foreground whitespace-pre-wrap">{load.special_instructions}</p>
          </div>
        )}
        {load.shipper_notes_to_brokers && (
          <div className="mt-3 border-t border-border pt-2">
            <p className="text-[10px] text-muted-foreground uppercase mb-1">Notes to Brokers</p>
            <p className="text-xs text-foreground whitespace-pre-wrap">{load.shipper_notes_to_brokers}</p>
          </div>
        )}
      </Card>

      {/* Stops */}
      {loadStops.length > 0 && (
        <Card className="p-4 lg:col-span-2">
          <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Stops ({loadStops.length})
          </h3>
          <ol className="space-y-3">
            {loadStops.map((s) => (
              <li key={s.id} className="border border-border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-brand-teal">
                    Stop {s.stop_number} · {s.stop_type}
                  </span>
                  <span className="text-xs text-muted-foreground">{fmtDate(s.scheduled_date)}</span>
                </div>
                <p className="text-sm font-medium">{s.name || `${s.city}, ${s.state}`}</p>
                {s.address && <p className="text-xs text-muted-foreground">{s.address}</p>}
                <p className="text-xs text-muted-foreground">{s.city}, {s.state}</p>
                {(s.scheduled_time_start || s.scheduled_time_end) && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Window: {s.scheduled_time_start || "Open"} – {s.scheduled_time_end || "Open"}
                  </p>
                )}
                {s.instructions && <p className="text-xs italic text-muted-foreground mt-1">"{s.instructions}"</p>}
              </li>
            ))}
          </ol>
        </Card>
      )}
    </div>
  );
}
